import { useState } from "react";
import { Field, Label, Switch } from "@headlessui/react";
import { ChartPieIcon } from "@heroicons/react/24/outline";
import {
	SchoolSpaViewModel,
	useAllowNormativeMutation,
	useForbidNormativeMutation,
} from "../schoolsApiSlice";

interface NormativeSettingProps {
	schoolInfo: SchoolSpaViewModel;
	setSchoolInfo: (schoolInfo: SchoolSpaViewModel) => void;
	setErrorMessage: (message: string) => void;
}

export default function NormativeSetting({
	schoolInfo,
	setSchoolInfo,
	setErrorMessage,
}: NormativeSettingProps) {
	const [enabled, setEnabled] = useState(schoolInfo.normativeReportsEnabled);

	const [callEnable] = useAllowNormativeMutation();
	const [callDisable] = useForbidNormativeMutation();

	function handleChange() {
		if (enabled) {
			callDisable(schoolInfo.id)
				.then(() => {
					setEnabled(false);
					setSchoolInfo({ ...schoolInfo, normativeReportsEnabled: false });
				})
				.catch((error) => {
					setErrorMessage(
						`Failed to save the percentile and stanine setting. ${error.data}`
					);
				});
		} else {
			callEnable(schoolInfo.id)
				.then(() => {
					setEnabled(true);
					setSchoolInfo({ ...schoolInfo, normativeReportsEnabled: true });
				})
				.catch((error) => {
					setErrorMessage(
						`Failed to save the percentile and stanine setting. ${error.data}`
					);
				});
		}
	}

	return (
		<div className="flex items-center">
			<ChartPieIcon className="size-18" />
			<div className="ml-4">
				<p>Display Percentile and Stanine:</p>
				<Field className="flex items-center ml-2 mt-1">
					<Label className="sr-only">Normative Scores Toggle</Label>
					<Switch
						checked={enabled}
						onChange={handleChange}
						name="normativeToggle"
						className="group relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
						<span
							aria-hidden="true"
							className="pointer-events-none inline-block size-6 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
						/>
					</Switch>
				</Field>
			</div>
		</div>
	);
}
